import { PanelRow, PanelCell, Grid, Cell } from '@teamsnap/teamsnap-ui';

import * as React from 'react';
import * as _ from 'lodash';

import FormElementHook from './form-element-hook';

const CreateElement = FormElementHook as new () => FormElementHook;

/**
 *  This function takes in a useForm response and registers and renders form fields for collecting mailing info.
 * @param useHookResponse (call useForm() and store that in a variable, and then pass that in here)
 */
const MailingAddress: React.FunctionComponent<{ useHookResponse: any }> = ({ useHookResponse }) => {
  const { register, getValues } = useHookResponse;

  const areMailingFieldsValid = (field: string) => {
    const values = getValues();
    const mailingAddress = values.mailingAddress;
    const mailingAddress2 = values.mailingAddress2;
    const mailingCity = values.mailingCity;
    const mailingState = values.mailingState;
    const mailingCountry = values.mailingCountry;
    const mailingZip = values.mailingZip;
    const fields = [mailingAddress, mailingAddress2, mailingCity, mailingState, mailingCountry, mailingZip];

    // 2 valid scenarios:
    //   1. No mailing fields are provided at all.
    //   2. One or more are provided, which requires others.
    // Map them to lengths.
    const filled = fields.map((field) => field && field.length > 0);
    // Are they all greater than 0?
    const allFilled = filled.reduce((acc, ele) => acc && ele);

    // Okay, then they're all filled out. Validation passes.
    if (allFilled) {
      return true;
    }

    // No? Now we check to make sure none of them are filled out.
    const empty = fields.concat([mailingAddress2]).map((field) => !field || field.length == 0);
    const allEmpty = empty.reduce((acc, ele) => acc && ele);

    // If they're all empty, thats fine.
    if (allEmpty) {
      return true;
    }

    // If we get here, they're not all empty, but they're not all filled.
    // Check to see if THIS field is filled. Dont error out this specific field if so.
    if (field && field.length > 0) {
      return true;
    }

    // Validation fails.
    return false;
  };

  return (
    <PanelRow isWithCells={true} mods="Panel-row--header">
      <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
        <div>
          <h4>Mailing Address</h4>
          <span className="u-fontSizeSm u-textNormal">For off-line registration payments</span>
        </div>
      </PanelCell>
      <PanelCell mods="u-size9of12">
        <Grid isWithGutter={true}>
          <Cell mods="u-size12of12">
            <CreateElement
              useHookResponse={useHookResponse}
              inputRef={register({
                required: false,
                validate: (value) => areMailingFieldsValid(value) || 'An address is required.',
              })}
              type="text"
              field="mailingAddress"
              label="Address Line 1"
              placeholder="Address Line 1"
            />
          </Cell>
        </Grid>
        <br />
        <Grid>
          <Cell mods="u-size12of12">
            <CreateElement
              useHookResponse={useHookResponse}
              inputRef={register({
                required: false,
              })}
              type="text"
              field="mailingAddress2"
              label="Address Line 2"
              placeholder="Address Line 2"
            />
          </Cell>
        </Grid>
        <br />
        <Grid isWithGutter={true}>
          <Cell mods="u-size6of12">
            <CreateElement
              useHookResponse={useHookResponse}
              inputRef={register({
                required: false,
                validate: (value) => areMailingFieldsValid(value) || 'A city is required.',
              })}
              type="text"
              field="mailingCity"
              label="City"
              placeholder="City"
            />
          </Cell>
          <Cell mods="u-size6of12">
            <CreateElement
              useHookResponse={useHookResponse}
              inputRef={register({
                required: false,
                validate: (value) => areMailingFieldsValid(value) || 'A state/province is required.',
              })}
              type="text"
              field="mailingState"
              label="State/Province"
              placeholder="State/Province"
            />
          </Cell>
        </Grid>
        <br />
        <Grid isWithGutter={true}>
          <Cell mods="u-size6of12">
            <CreateElement
              useHookResponse={useHookResponse}
              inputRef={register({
                required: false,
                validate: (value) => areMailingFieldsValid(value) || 'A country is required.',
              })}
              type="country"
              field="mailingCountry"
              placeholder="Country"
              label="Country"
            />
          </Cell>
          <Cell mods="u-size6of12">
            <CreateElement
              useHookResponse={useHookResponse}
              inputRef={register({
                required: false,
                validate: (value) => areMailingFieldsValid(value) || 'A zip/postal code is required.',
              })}
              type="text"
              field="mailingZip"
              placeholder="Zip/Postal Code"
              label="Zip/Postal Code"
            />
          </Cell>
        </Grid>
        <br />
        <Grid>
          <Cell mods="u-size12of12">
          <CreateElement
            useHookResponse={useHookResponse}
            inputRef={register({
              required: false,
            })}
            type="checkbox"
            field="updateAllSeasonsAddress"
            label="Apply the org address to all active seasons. Updating the season address may change offline payment instructions."
          />
          </Cell>
        </Grid>
      </PanelCell>
    </PanelRow>
  );
};

export default MailingAddress;
